import { Box, Typography, IconButton, Checkbox, FormControlLabel } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import useYourNotesNode from './useYourNotesNode';

const PAGE_SIZE = 25;

/**
 * YourNotesNodePager
 *
 * Prev/next pager for navigating pages within the active node.
 * Also renders a "hide completed" checkbox when completed entries are present.
 *
 * Props:
 * - hideCompleted: boolean — current hide state
 * - setHideCompleted: fn — toggle handler from YourNotesNode
 * - completedCount: number — how many completed entries are in the current page
 */
export default function YourNotesNodePager({ showCompleted, setShowCompleted, completedCount }) {
    const { activeNode, nodes, loadPage, getCurrentPage } = useYourNotesNode();

    // Find the active node descriptor to get total item count for page calculation
    const node = nodes.find((d) => d.key === activeNode);
    const total = node?.total ?? 0;
    const totalPages = Math.ceil(total / PAGE_SIZE) || 1;
    const currentPage = getCurrentPage(activeNode);

    // Calculates next page and delegates to engine — no BL, guard only for out-of-range
    const handlePage = (direction) => {
        const next = currentPage + direction;
        if (next < 1 || next > totalPages) return;
        loadPage(activeNode, next);
    };

    return (
        <Box className="yn-node-pager" sx={{ justifyContent: 'space-between', px: 1 }}>
            {/* Left — hide completed checkbox, only when relevant */}
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                {completedCount > 0 && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={showCompleted}
                                onChange={(e) => setShowCompleted(e.target.checked)}
                                sx={{
                                    color: '#a3d1ff',
                                    '&.Mui-checked': { color: '#a3d1ff' },
                                    padding: '2px 4px',
                                }}
                            />
                        }
                        label={
                            <Typography variant="caption" sx={{ color: '#a3d1ff', fontSize: '11px' }}>
                                Completed ({completedCount})
                            </Typography>
                        }
                        sx={{ margin: 0 }}
                    />
                )}
            </Box>

            {/* Right — prev / page indicator / next */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                <IconButton size="small" onClick={() => handlePage(-1)} disabled={currentPage <= 1} sx={{ color: '#a3d1ff' }}>
                    <ChevronLeft fontSize="small" />
                </IconButton>
                <Typography variant="caption" sx={{ color: '#a3d1ff', fontSize: '11px', whiteSpace: 'nowrap' }}>
                    {currentPage} / {totalPages}
                    {!showCompleted && completedCount > 0 && (
                        <span style={{ color: '#6b8fa8', marginLeft: 4 }}>({completedCount} hidden)</span>
                    )}
                </Typography>
                <IconButton size="small" onClick={() => handlePage(1)} disabled={currentPage >= totalPages} sx={{ color: '#a3d1ff' }}>
                    <ChevronRight fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    );
}
